import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { PublicRoute } from './components/PublicRoute';
import { ProtectedRoute } from './components/ProtectedRoute';
import { HealthCoachDashboard } from './components/HealthCoachDashboard/HealthCoachDashboard';
import { useModal } from './hooks/useModal';
import { UserDetailsRoute } from './components/UserDashboard/UserDetailsRoute';
import ContentPreview from './components/ContentPreview/ContentPreview';
import { LandingPage } from './components/LandingPage/LandingPage';
import { AppLayout } from './AppLayout';
import { NotFoundPage } from './components/NotFoundView/NotFoundPage';
import { SupportPage } from './components/SupportPage/SupportPage';
import { TasksPage } from './components/TasksPage/TasksPage';
import CalendarPage from './components/CalendarPage/CalendarPage';
import { ProviderHome } from './components/ProviderHome/ProviderHome';
import { PatientProfileRoute } from './components/PatientProfile/PatientProfileRoute';
import { DesignSystemPage } from './components/DesignSystem/DesignSystemPage';
import { GlobalBar } from './components/GlobalBar/GlobalBar';
import { PrototypeBar } from './components/PrototypeBar/PrototypeBar';
import { PrototypeStateProvider } from './components/PrototypeBar/PrototypeStateContext';
import { Box, Flex } from '@chakra-ui/react';

// Workspace tooling (the top bar + the design-system audit) is for the
// product & design team, not clinicians. It ships on every build but only
// mounts outside production, so it can never surface in the patient-facing
// app even if this code is deployed there.
const workspaceToolingEnabled =
  process.env.REACT_APP_ENV_LABEL !== 'production';

export const Root = () => {
  const modalToRender = useModal();

  return (
    <>
      {modalToRender}

      <Router>
        {/* Prototype demo state is provided above the router outlet so it
            survives navigation and so the controls in the workspace chrome and
            their consumers inside the clinician experience share one source of
            truth. The provider always mounts; only its bar UI is gated. */}
        <PrototypeStateProvider>
        <Flex direction="column" position="fixed" inset={0} minH={0}>
          {workspaceToolingEnabled && (
            <>
              <GlobalBar />
              <PrototypeBar />
            </>
          )}

          <Box flex={1} minH={0}>
            <Routes>
              {/* Workspace-level route: full-bleed under the GlobalBar,
                  outside the clinician SideNav chrome. Non-production only. */}
              {workspaceToolingEnabled && (
                <Route
                  path="/design-system"
                  element={
                    <ProtectedRoute>
                      <DesignSystemPage />
                    </ProtectedRoute>
                  }
                />
              )}

              {/* Clinician (Care OS) experience — SideNav + content. */}
              <Route element={<AppLayout />}>
                <Route
                  path="/"
                  element={
                    <PublicRoute>
                      <LandingPage />
                    </PublicRoute>
                  }
                />

                <Route path="/content-preview" element={<ContentPreview />} />

                <Route
                  path="/provider-home"
                  element={
                    <ProtectedRoute>
                      <ProviderHome />
                    </ProtectedRoute>
                  }
                />

                {/* careOS patient profile + labs workflow */}
                <Route
                  path="/provider-home/patient/:patientId"
                  element={
                    <ProtectedRoute>
                      <PatientProfileRoute />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/hc-dashboard"
                  element={
                    <ProtectedRoute>
                      <HealthCoachDashboard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/support"
                  element={
                    <ProtectedRoute>
                      <SupportPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/tasks"
                  element={
                    <ProtectedRoute>
                      <TasksPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/calendar"
                  element={
                    <ProtectedRoute>
                      <CalendarPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/user-details/:patient/*"
                  element={
                    <ProtectedRoute>
                      <UserDetailsRoute />
                    </ProtectedRoute>
                  }
                />

                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </Box>
        </Flex>
        </PrototypeStateProvider>
      </Router>
    </>
  );
};
